<script lang="ts">
    import { trans } from '$assets/translator';
    import CheckCircle from '$backoffice/icons/CheckCircle.svelte';
    import Forbid from '$backoffice/icons/Forbid.svelte';
    import Timer from '$backoffice/icons/Timer.svelte';
    import cn from '$common/utils/cn';

    export let booking: {
        dates: {
            createdAt: string | null;
            abandonedAt: string | null;
            quoteSentAt: string | null;
            quoteExpirationDate: string | null;
            bookedAt: string | null;
            confirmedAt: string | null;
            travelPlanStartDate: string | null;
            travelPlanEndDate: string | null;
        };
        status: string;
    };

    const stepDefinitions = [
        {
            key: 'creation',
            label: trans('backpack.booking.booking_item.steps.creation'),
            dateProp: 'createdAt',
        },
        {
            key: 'quoteSent',
            label: trans('backpack.booking.booking_item.steps.quoteSent'),
            dateProp: 'quoteSentAt',
        },
        {
            key: 'quoteExpiry',
            label: trans('backpack.booking.booking_item.steps.quoteExpiry'),
            dateProp: 'quoteExpirationDate',
        },
        {
            key: 'booking',
            label: trans('backpack.booking.booking_item.steps.booking'),
            dateProp: 'bookedAt',
        },
        {
            key: 'confirmation',
            label: trans('backpack.booking.booking_item.steps.confirmation'),
            dateProp: 'confirmedAt',
        },
        {
            key: 'departure',
            label: trans('backpack.booking.booking_item.steps.departure'),
            dateProp: 'travelPlanStartDate',
        },
        {
            key: 'return',
            label: trans('backpack.booking.booking_item.steps.return'),
            dateProp: 'travelPlanEndDate',
        },
    ];

    // Returns YYYY-MM-DD in the business timezone, safe for lexicographic date comparison.
    function toDateString(date: Date): string {
        return date.toLocaleDateString('sv', { timeZone: 'Europe/Paris' });
    }

    const calculatedSteps = (() => {
        // Get today's date (once) set to midnight for date-only comparison.
        const today = new Date();

        // --- Step 1: Determine if each step's date is past or today ---
        // Iterate through steps, only checking the date condition. Status is ignored here.
        const stepsWithDateCheck = stepDefinitions.map((stepDef) => {
            const dateValue =
                booking.dates[stepDef.dateProp as keyof typeof booking.dates];
            let isDatePastOrToday = false; // Assume false by default

            // Check if date exists and is valid
            if (dateValue) {
                try {
                    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- local copy, never reactive state
                    const stepDate = new Date(dateValue);
                    if (!isNaN(stepDate.getTime())) {
                        stepDate.setHours(0, 0, 0, 0);
                        // Mark true if the step's date is today or in the past
                        isDatePastOrToday =
                            toDateString(stepDate) <= toDateString(today);
                    }
                } catch (e) {
                    console.error(
                        'Error parsing step date for active check:',
                        dateValue,
                        e,
                    );
                }
            }

            return {
                ...stepDef,
                date: dateValue,
                isDatePastOrToday: isDatePastOrToday, // Store this intermediate boolean result
            };
        });

        // --- Step 2: Find the index of the *last* step whose date was past or today ---
        // This determines the overall progress shown visually.
        const lastActiveIndexBasedOnDate = stepsWithDateCheck.findLastIndex(
            (step) => step.isDatePastOrToday,
        );

        // --- Step 3: Create the final steps array for the template ---
        // A step is marked 'active' for styling if its index is less than or equal to
        // the index of the last step whose date condition was met.
        const finalSteps = stepsWithDateCheck.map((step, index) => {
            return {
                ...step,
                active: index <= lastActiveIndexBasedOnDate,
            };
        });

        return {
            steps: finalSteps,
            lastActiveIndex: lastActiveIndexBasedOnDate,
        };
    })();

    const progressWidth = (() => {
        const { lastActiveIndex } = calculatedSteps;
        const totalSteps = stepDefinitions.length;

        if (lastActiveIndex === -1) {
            return 0;
        }
        if (lastActiveIndex === totalSteps - 1) {
            return 100;
        }

        const progress = ((lastActiveIndex + 0.5) / totalSteps) * 100;
        return Math.min(progress, 100);
    })();

    const widthValue =
        progressWidth !== 0 && progressWidth !== 100
            ? `calc(${progressWidth}% + 12px)`
            : `${progressWidth}%`;

    const barColorClass = getBarColorClass(
        booking.status,
        calculatedSteps.steps,
        calculatedSteps.lastActiveIndex,
    );

    const isCancelledOrAbandoned =
        booking.status === 'cancelled' || booking.status === 'abandoned';

    function getBarColorClass(
        status: string,
        // Pass the calculated steps array and the last active index
        steps: Array<{
            key: string;
            label: string;
            dateProp: string;
            date: string | null;
            active: boolean;
        }>,
        lastActiveIndex: number,
    ): string {
        // Get today's date, set to midnight for date-only comparison
        const today = new Date();

        // Check if the last active step exists and is 'quoteExpiry'
        if (
            lastActiveIndex !== -1 &&
            steps[lastActiveIndex]?.key === 'quoteExpiry'
        ) {
            const quoteExpiryStep = steps[lastActiveIndex];
            // Check if the expiry date exists
            if (quoteExpiryStep.date) {
                try {
                    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- local copy, never reactive state
                    const expiryDate = new Date(quoteExpiryStep.date);
                    if (!isNaN(expiryDate.getTime())) {
                        expiryDate.setHours(0, 0, 0, 0);
                        // Check if the expiry date is strictly BEFORE today
                        if (toDateString(expiryDate) < toDateString(today)) {
                            // If yes, return the warning color regardless of booking.status
                            return 'bg-status-warning';
                        }
                    }
                } catch (e) {
                    console.error(
                        'Error parsing quoteExpirationDate for color check:',
                        quoteExpiryStep.date,
                        e,
                    );
                }
            }
        }
        switch (status) {
            case 'abandoned':
            case 'cancelled':
            case 'draft':
                return 'bg-content-quiet/30';
            default:
                return 'bg-container-brand';
        }
    }

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '--';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
                return '--';
            }
            // Use Intl for robust formatting (DD/MM/YYYY for fr-FR)
            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'Europe/Paris',
            }).format(date);
        } catch (e) {
            console.error('Error formatting date:', dateStr, e);
            return '--';
        }
    }
</script>

<div class="relative w-full overflow-hidden bg-white px-m pt-m">
    <div class="relative z-30 flex justify-between text-xs">
        {#each calculatedSteps.steps as step, i (step.key)}
            <div class="step flex-1 px-2xs text-center">
                <span class="block text-content-quiet">{step.label}</span>
                <span class="mt-xs block">{formatDate(step.date)}</span>

                <div
                    class="relative flex w-full translate-y-5 items-center justify-center"
                >
                    {#if calculatedSteps.steps && calculatedSteps.steps[i]}
                        {@const isActiveStep = calculatedSteps.steps[i].active}
                        {@const isLastStep =
                            i === calculatedSteps.steps.length - 1}
                        {@const isLastActiveStep =
                            i === calculatedSteps.lastActiveIndex}

                        {#if isLastStep && isLastActiveStep}
                            <CheckCircle
                                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4"
                            />
                        {:else if step.key === 'quoteExpiry' && isLastActiveStep}
                            <Timer
                                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4"
                            />
                        {:else if isCancelledOrAbandoned && (isLastActiveStep || (calculatedSteps.lastActiveIndex === -1 && i === 0))}
                            <Forbid
                                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4"
                            />
                        {:else}
                            <span
                                class={cn(
                                    'inline-block size-xs rounded-full transition-colors duration-300 ease-in-out',
                                    {
                                        'bg-white': isActiveStep,
                                        'bg-content-quiet': !isActiveStep,
                                    },
                                )}
                                aria-hidden="true"
                            ></span>
                        {/if}
                    {/if}
                </div>
            </div>
        {/each}
    </div>
    <div class="relative mt-m h-xl w-full">
        <div
            class="absolute inset-0 z-10 w-full -translate-y-1/2 rounded-full bg-container-quiet"
            aria-hidden="true"
        ></div>
        <div
            class="absolute left-0 top-0 h-xl {barColorClass} z-20 -translate-y-1/2 rounded-full"
            style="width: {widthValue};"
            aria-hidden="true"
        ></div>
    </div>
</div>
